import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./AuditPage.css";

type ActivityRow = {
  event_id: string;
  event_type: string;
  event_action: string;
  entity_id: string | null;
  title: string;
  detail: string | null;
  actor_id: string | null;
  occurred_at: string;
};

const LABELS: Record<string, string> = {
  quotation: "Cotización",
  service_order: "Taller",
  inventory: "Inventario",
  sale: "Venta",
  purchase: "Compra",
  accounting: "Contabilidad",
  treasury: "Tesorería",
  treasury_transfer: "Transferencia",
  bank_reconciliation: "Conciliación",
};

function humanizeAudit(row: ActivityRow) {
  const titleMap: Record<string, string> = {
    document: "Comprobante",
    document_item: "Detalle de comprobante",
    quotation: "Cotización",
    service_order: "Orden de servicio",
    inventory: "Movimiento de inventario",
    sale: "Venta",
    purchase: "Compra",
    accounting: "Movimiento contable",
    treasury: "Movimiento de tesorería",
    treasury_transfer: "Transferencia",
    bank_reconciliation: "Conciliación bancaria",
  };

  const actionMap: Record<string, string> = {
    created: "Creado",
    updated: "Actualizado",
    deleted: "Eliminado",
    inserted: "Creado",
    status_changed: "Estado actualizado",
    issued: "Emitido",
    cancelled: "Anulado",
    posted: "Posteado",
  };

  let detail = row.detail?.trim() || actionMap[row.event_action] || row.event_action;

  if (
    detail.startsWith("[") ||
    detail.startsWith("{") ||
    /_at|tax_total|taxable_amount|subtotal|updated_at/.test(detail)
  ) {
    try {
      const parsed = JSON.parse(detail);
      if (Array.isArray(parsed)) {
        detail = `Campos actualizados: ${parsed
          .map((value) =>
            String(value)
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase()),
          )
          .join(", ")}.`;
      } else {
        detail = actionMap[row.event_action] || "Registro actualizado.";
      }
    } catch {
      detail = actionMap[row.event_action] || "Registro actualizado.";
    }
  }

  const rawTitle = row.title?.trim();
  const title =
    titleMap[rawTitle] ??
    titleMap[row.event_type] ??
    rawTitle?.replace(/_/g, " ") ??
    "Actividad";

  return { title, detail };
}

export function AuditPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_organization_activity_feed",
      {
        p_organization_id: organizationId,
        p_limit: 200,
      },
    );

    if (rpcError) {
      setRows([]);
      setError(rpcError.message);
      setLoading(false);
      return;
    }

    setRows((data ?? []) as ActivityRow[]);
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  const visible = filter
    ? rows.filter((row) => row.event_type === filter)
    : rows;

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Auditoría</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Actividad reciente consolidada de los módulos operativos.</p>
        </div>

        <button className="ghost-button" type="button" onClick={() => void load()}>
          Actualizar
        </button>
      </header>

      <section className="audit-filters">
        <button
          className={filter === "" ? "compact-button active-filter" : "compact-button"}
          type="button"
          onClick={() => setFilter("")}
        >
          Todo
        </button>
        {[
          "quotation",
          "service_order",
          "inventory",
          "sale",
          "purchase",
          "treasury",
          "treasury_transfer",
          "bank_reconciliation",
          "accounting",
        ].map((type) => (
            <button
              key={type}
              className={filter === type ? "compact-button active-filter" : "compact-button"}
              type="button"
              onClick={() => setFilter(type)}
            >
              {LABELS[type]}
            </button>
          ),
        )}
      </section>

      {error ? <div className="inline-alert">{error}</div> : null}

      {!loading && visible.length === 0 && !error ? (
        <section className="empty-state">
          <strong>No hay actividad para mostrar.</strong>
        </section>
      ) : null}

      <section className="audit-timeline">
        {visible.map((row) => {
          const display = humanizeAudit(row);
          return (
            <article className="audit-event" key={row.event_type + "-" + row.event_id}>
              <div className="audit-marker" />
              <div className="audit-event-body">
                <div className="audit-event-heading">
                  <span className="status-badge">{LABELS[row.event_type] ?? "Actividad"}</span>
                  <time>{new Date(row.occurred_at).toLocaleString("es-PE")}</time>
                </div>
                <h2>{display.title}</h2>
                <p>{display.detail}</p>
              </div>
            </article>
          );
        })}
      </section>

      {loading ? (
        <section className="empty-state">
          <strong>Cargando actividad…</strong>
        </section>
      ) : null}
    </div>
  );
}
